import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInversiones } from '../hooks/useInversiones';
import { LoggerService } from '../../../shared/services/logger.service';
import type { TipoInversion } from '../../../shared/types';
import { MetricCard, EmptyState, LoadingSpinner, ActionButton } from '../../../shared/components';
import AdvancedChart from '../../../components/feature/AdvancedChart';

interface InversionFormData {
  concepto: string;
  tipo: TipoInversion | '';
  monto: number;
  fecha: string;
  descripcion?: string;
}

interface InversionMetrics {
  totalInvertido: number;
  rendimientoPromedio: number;
  instrumentosActivos: number;
}

export const ModernInversionesDashboard: React.FC = () => {
  const { inversiones, loading, error, createInversion, deleteInversion } = useInversiones();
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState<string>('');
  const [formData, setFormData] = useState<InversionFormData>({
    concepto: '',
    tipo: '',
    monto: 0,
    fecha: new Date().toISOString().split('T')[0],
    descripcion: ''
  });

  // Detectar filtro de tipo desde URL
  useEffect(() => {
    const tipo = searchParams.get('tipo');
    if (tipo) {
      setTipoFiltro(tipo);
    }
  }, [searchParams]);

  // Filtrar inversiones por tipo
  const inversionesFiltradas = tipoFiltro 
    ? inversiones.filter(inversion => 
        inversion.tipo.toLowerCase().replace('_', ' ') === tipoFiltro.toLowerCase()
      )
    : inversiones;

  const limpiarFiltro = () => {
    setTipoFiltro('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyUSD = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Procesar datos de inversiones agrupados por tipo
  const inversionesData = useMemo(() => {
    if (!inversiones || inversiones.length === 0) {
      return [];
    }

    const groupedInversiones = inversiones.reduce((acc, inversion) => {
      const tipo = inversion.tipo || 'otros';
      if (!acc[tipo]) {
        acc[tipo] = 0;
      }
      acc[tipo] += inversion.monto;
      return acc;
    }, {} as Record<string, number>);

    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];
    return Object.entries(groupedInversiones).map(([tipo, total], index) => ({
      label: tipo.charAt(0).toUpperCase() + tipo.slice(1).replace('_', ' '),
      value: total,
      color: colors[index % colors.length]
    }));
  }, [inversiones]);

  const getMetrics = (): InversionMetrics => {
    const totalInvertido = inversiones.reduce((total, inv) => total + inv.monto, 0);
    const rendimientoPromedio = 10.9; // Calculado dinámicamente en una implementación real
    const instrumentosActivos = inversiones.length;
    
    return {
      totalInvertido,
      rendimientoPromedio,
      instrumentosActivos
    };
  };

  const getInversionIcon = (tipo: TipoInversion) => {
    const icons: { [key in TipoInversion]: string } = {
      'fondo_mutuo': 'ri-pie-chart-line',
      'acciones': 'ri-line-chart-line',
      'bonos': 'ri-file-chart-line',
      'criptomonedas': 'ri-currency-line',
      'cedears': 'ri-global-line',
      'obligacion_negociable': 'ri-article-line',
      'otros': 'ri-money-dollar-circle-line'
    };
    return icons[tipo] || 'ri-money-dollar-circle-line';
  };

  const getInversionColor = (tipo: TipoInversion) => {
    const colors: { [key in TipoInversion]: { bg: string; text: string; icon: string } } = {
      'fondo_mutuo': { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
      'acciones': { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-600' },
      'bonos': { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600' },
      'criptomonedas': { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-600' },
      'cedears': { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600' },
      'obligacion_negociable': { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'text-teal-600' },
      'otros': { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'text-gray-600' }
    };
    return colors[tipo] || colors['otros'];
  };

  const getVariationColor = () => {
    // Simulamos variación aleatoria ya que no está en el modelo
    const variacion = Math.random() > 0.5 ? 1 : -1;
    return variacion >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const handleInputChange = (field: keyof InversionFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      LoggerService.info('Creating new inversion', 'ModernInversionesDashboard', { formData });
      await createInversion({
        concepto: formData.concepto,
        tipo: formData.tipo as TipoInversion,
        monto: formData.monto,
        fecha: new Date(formData.fecha),
        descripcion: formData.descripcion
      });
      
      // Reset form
      setFormData({
        concepto: '',
        tipo: '',
        monto: 0,
        fecha: new Date().toISOString().split('T')[0],
        descripcion: ''
      });
      setShowForm(false);
      
      LoggerService.info('Inversion created successfully', 'ModernInversionesDashboard');
    } catch (error) {
      LoggerService.error('Failed to create inversion', 'ModernInversionesDashboard', { error });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      LoggerService.info('Deleting inversion', 'ModernInversionesDashboard', { id });
      await deleteInversion(id);
      LoggerService.info('Inversion deleted successfully', 'ModernInversionesDashboard');
    } catch (error) {
      LoggerService.error('Failed to delete inversion', 'ModernInversionesDashboard', { error });
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Cargando inversiones..." />;
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  const metrics = getMetrics();

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inversiones</h1>
          <p className="text-gray-600 mt-1">Administra tu cartera de inversiones</p>
        </div>
        
        <ActionButton
          onClick={() => setShowForm(true)}
          icon="ri-add-line"
          text="Nueva Inversión"
          variant="success"
        />
      </div>

      {/* Metrics Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <MetricCard
          title="Total Invertido"
          value={formatCurrency(metrics.totalInvertido)}
          icon="ri-money-dollar-circle-line"
          iconColor="emerald"
        />
        
        <MetricCard
          title="Rendimiento Promedio"
          value={`+${metrics.rendimientoPromedio}%`}
          icon="ri-trending-up-line"
          iconColor="green"
          change={{ value: 2.1, type: 'positive' }}
        />
        
        <MetricCard
          title="Instrumentos Activos"
          value={metrics.instrumentosActivos.toString()}
          icon="ri-bar-chart-box-line"
          iconColor="blue"
        />
      </div>

      {/* Mi Cartera */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {tipoFiltro ? `Inversiones en ${tipoFiltro}` : 'Mi Cartera'}
              </h2>
              {tipoFiltro && (
                <p className="text-sm text-gray-500 mt-1">
                  {inversionesFiltradas.length} inversión{inversionesFiltradas.length !== 1 ? 'es' : ''} encontrada{inversionesFiltradas.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            {tipoFiltro && (
              <button
                onClick={limpiarFiltro}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <i className="ri-close-line mr-1"></i>
                Limpiar filtro
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {inversionesFiltradas.length === 0 ? (
            <EmptyState
              icon="ri-pie-chart-line"
              title={tipoFiltro ? `No hay inversiones en "${tipoFiltro}"` : "No hay inversiones registradas"}
              description={tipoFiltro ? "Prueba con otro tipo de inversión" : "Agrega tu primera inversión para comenzar a administrar tu cartera"}
              action={!tipoFiltro ? {
                text: "Nueva Inversión",
                onClick: () => setShowForm(true)
              } : undefined}
            />
          ) : (
            <div className="space-y-4">
              {inversionesFiltradas.map((inversion) => {
                const colorScheme = getInversionColor(inversion.tipo);
                const icon = getInversionIcon(inversion.tipo);
                const variationColor = getVariationColor();
                const randomVariation = (Math.random() * 20 - 10).toFixed(1); // Variación simulada entre -10% y 10%
                
                return (
                  <div
                    key={inversion.id}
                    className={`${colorScheme.bg} rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 ${colorScheme.bg} rounded-lg flex items-center justify-center border`}>
                          <i className={`${icon} ${colorScheme.icon} text-xl`}></i>
                        </div>
                        
                        <div>
                          <h3 className="font-semibold text-gray-900">{inversion.concepto}</h3>
                          <p className={`text-sm ${colorScheme.text} capitalize`}>{inversion.tipo.replace('_', ' ')}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {inversion.tipo === 'criptomonedas' 
                            ? formatCurrencyUSD(inversion.monto) 
                            : formatCurrency(inversion.monto)
                          }
                        </p>
                        <p className={`text-sm font-medium ${variationColor}`}>
                          {parseFloat(randomVariation) >= 0 ? '+' : ''}{randomVariation}%
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleDelete(inversion.id)}
                        className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar inversión"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Distribución por Tipo */}
      {inversionesData.length > 0 ? (
        <AdvancedChart 
          data={inversionesData} 
          type="investment" 
          title="Distribución por Tipo"
          allowLineChart={true}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Distribución por Tipo</h2>
          </div>
          <div className="p-6">
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-gray-600">No hay inversiones registradas</p>
              <p className="text-sm text-gray-500 mt-1">Agrega tu primera inversión para ver la distribución</p>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Nueva Inversión</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto
                </label>
                <input
                  type="text"
                  value={formData.concepto}
                  onChange={(e) => handleInputChange('concepto', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Ej: Fondo Común de Inversión"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="fondo_mutuo">Fondo Mutuo</option>
                  <option value="acciones">Acciones</option>
                  <option value="bonos">Bonos</option>
                  <option value="criptomonedas">Criptomonedas</option>
                  <option value="cedears">CEDEARs</option>
                  <option value="otros">Otros</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto Invertido
                </label>
                <input
                  type="number"
                  value={formData.monto}
                  onChange={(e) => handleInputChange('monto', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleInputChange('fecha', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={2}
                  placeholder="Información adicional..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.concepto || !formData.tipo || formData.monto <= 0}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};